import Pagination from '@/components/Pagination';
import { MissionSummary } from '@edwin-group/cj-graphql';
import { usePagination } from '../../../cohort-summary/hooks/usePagination';
import { useMemo } from 'react';
import { useReportingToolsStore } from '@/pages/reporting-tools/store/useReportingToolsStore';

const getTraitImprovementColor = (traitImprovement: number) => {
  if (traitImprovement > 0) return 'text-green-500';
  if (traitImprovement < 0) return 'text-red-500';
  return 'text-gray-500';
};

export const Table = ({ missionSummary }: { missionSummary: MissionSummary }) => {
  const missions = useMemo(() => missionSummary?.missions?.flatMap((e) => e?.raws), [missionSummary?.missions]);
  const { currentPage, totalPages, currentPageData, handlePageChange } = usePagination(missions);

  const page = useReportingToolsStore((state) => state.page);

  const KEYS_HEADER = useMemo(
    () => [
      page?.missionSummary?.table?.missionName,
      page?.missionSummary?.table?.trait,
      page?.missionSummary?.table?.date,
      page?.missionSummary?.table?.cohortName,
      page?.missionSummary?.table?.assessmentStatus,
      page?.missionSummary?.table?.traitImprovement,
      // page?.missionSummary?.table?.preAssessment,
      // page?.missionSummary?.table?.postAssessment,
      page?.missionSummary?.table?.preObservationalNotes,
      page?.missionSummary?.table?.postObservationalNotes,
    ],
    [page?.missionSummary?.table],
  );

  const getNoteContent = (note?: string | null) => {
    const trimmedNote = note?.trim();
    if (!trimmedNote) return '-';

    return (
      <div className="assessment-notes-scroll max-h-[140px] overflow-y-auto whitespace-pre-wrap break-words pr-1">
        {trimmedNote}
      </div>
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="border-b border-gray-200 bg-gray-50">
          <tr>
            {KEYS_HEADER.map((headerItem, index) => (
              <th
                key={index}
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                {headerItem}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 ">
          {currentPageData.map((cohortSummary, index) => {
            return (
              <tr key={index} className="hover:bg-gray-50 [&>td]:align-top">
                <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                  {cohortSummary?.roleModel || '-'}
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                  {cohortSummary?.trait || '-'}
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                  {cohortSummary?.startedAt
                    ? new Date(cohortSummary.startedAt + 'T00:00:00-03:00').toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })
                    : '-'}
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                  {cohortSummary?.cohortName || '-'}
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                  {cohortSummary?.assessmentStatus || '-'}
                </td>
                <td
                  className={`whitespace-nowrap px-6 py-4 text-sm text-gray-500 ${getTraitImprovementColor(
                    cohortSummary?.traitImprovement || 0,
                  )}`}>
                  % {cohortSummary?.traitImprovement != null ? Math.round(Number(cohortSummary.traitImprovement)) : '-'}
                </td>
                {/* <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                  {cohortSummary?.preAssessment || '-'}
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                  {cohortSummary?.postAssessment || '-'}
                </td> */}
                <td className="max-w-[300px] px-6 py-4 text-sm text-gray-500">
                  {getNoteContent(cohortSummary?.preObservationNotes)}
                </td>
                <td className="max-w-[300px] px-6 py-4 text-sm text-gray-500">
                  {getNoteContent(cohortSummary?.postObservationNotes)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {totalPages > 1 && (
        <div className="border-t border-gray-200 bg-white px-4 py-3">
          <Pagination currentPage={currentPage} pageSize={totalPages} onChangePage={handlePageChange} />
        </div>
      )}
    </div>
  );
};
